import React, { useEffect, useRef, useState } from "react";
import * as S from "./IASistemasStyle";
import { iaService } from "../../Services/iaService";

/**
 * IA interna de relatórios — a equipe pergunta em linguagem natural
 * ("quantos contratos fechamos esse mês?") e a IA responde com os
 * números reais do sistema.
 */
export default function RelatoriosTab() {
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [pergunta, setPergunta] = useState("");
  const [asking, setAsking] = useState(false);
  const scrollRef = useRef(null);

  const loadChats = async () => {
    try {
      setChats(await iaService.getInternalChats());
    } catch (e) {
      console.error("Erro ao listar consultas:", e);
    }
  };

  const loadTurns = async (chatId) => {
    try {
      setTurns(await iaService.getInternalChatMessages(chatId));
    } catch (e) {
      console.error("Erro ao carregar consulta:", e);
    }
  };

  useEffect(() => {
    loadChats();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, asking]);

  const handleSelect = (chat) => {
    setSelectedChatId(chat.id);
    loadTurns(chat.id);
  };

  const handleNew = () => {
    setSelectedChatId(null);
    setTurns([]);
  };

  const handleAsk = async () => {
    const q = pergunta.trim();
    if (!q || asking) return;
    setAsking(true);
    setPergunta("");
    // Mostra a pergunta imediatamente
    setTurns((prev) => [
      ...prev,
      { id: `tmp-${Date.now()}`, client_message: q, ai_message: null },
    ]);

    try {
      const res = await iaService.askInternalReport(q, selectedChatId);
      setSelectedChatId(res.chat_id);
      await loadTurns(res.chat_id);
      await loadChats();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao consultar a IA de relatórios.");
      setTurns((prev) => prev.filter((t) => !String(t.id).startsWith("tmp-")));
    } finally {
      setAsking(false);
    }
  };

  return (
    <S.ChatLayout>
      <S.ChatListPanel>
        <S.NewChatButton onClick={handleNew}>+ Nova Consulta</S.NewChatButton>
        <S.ChatListScroll>
          {chats.length === 0 ? (
            <S.LoadingText>Nenhuma consulta ainda.</S.LoadingText>
          ) : (
            chats.map((chat) => (
              <S.ChatListItem
                key={chat.id}
                $selected={selectedChatId === chat.id}
                onClick={() => handleSelect(chat)}
              >
                <div className="nome">{chat.name}</div>
                <div className="preview">
                  {chat.created_by ? `por ${chat.created_by} • ` : ""}
                  {chat.date_created
                    ? new Date(chat.date_created).toLocaleString("pt-BR")
                    : ""}
                </div>
              </S.ChatListItem>
            ))
          )}
        </S.ChatListScroll>
      </S.ChatListPanel>

      <S.ChatWindow>
        <S.ChatHeader>
          <div className="info">
            <strong>Relatórios com IA</strong>
            <span>
              Pergunte em linguagem natural: contratos, saques, clientes,
              caixinhas, leads...
            </span>
          </div>
        </S.ChatHeader>

        <S.MessagesScroll ref={scrollRef}>
          {turns.length === 0 && !asking ? (
            <S.EmptyState>
              <strong style={{ color: "#5b6270", fontSize: 15 }}>
                Consultas gerenciais em linguagem natural
              </strong>
              Exemplos: "Quantos contratos fechamos esse mês e qual o valor total?"
              <br />
              "Quanto saiu de saque nos últimos 30 dias?" · "Quantos clientes novos essa semana?"
            </S.EmptyState>
          ) : (
            turns.map((t) => (
              <React.Fragment key={t.id}>
                <S.Bubble $fromIA={false}>{t.client_message}</S.Bubble>
                {t.ai_message ? (
                  <S.Bubble $fromIA>{t.ai_message}</S.Bubble>
                ) : (
                  asking && <S.Bubble $fromIA>Analisando os dados...</S.Bubble>
                )}
              </React.Fragment>
            ))
          )}
        </S.MessagesScroll>

        <S.InputRow>
          <input
            placeholder="Faça uma pergunta sobre os dados do negócio..."
            value={pergunta}
            onChange={(e) => setPergunta(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAsk()}
            disabled={asking}
          />
          <S.ActionButton $variant="gold" onClick={handleAsk} disabled={asking}>
            {asking ? "Consultando..." : "Perguntar"}
          </S.ActionButton>
        </S.InputRow>
      </S.ChatWindow>
    </S.ChatLayout>
  );
}
